import { ChangeDetectorRef, Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { DeviceService } from '../../services/devices.service';
import { ChartOptions, Chart } from 'chart.js';
import { UIChart } from 'primeng/chart';
import { Output, EventEmitter } from '@angular/core';


interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor: string[];
    borderWidth: number;
    fill: boolean;
  }[];
}

interface Location {
  id: string;
  label: string;
}

interface Device {
  serialnumber: string;
  id: string;
  location: Location;
  model: string;
  announcement: string;
  label: string;
  status: string;
  selected: boolean;
  color: string;
}

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.css']
})
export class ChartComponent {

  @Input() selectedDeviceIDs: number[] = [];
  @Input() selectedMeasure: string = '';
  @Input() selectedDuration: string | null = '';
  @Input() iaqDevices: Device[] = [];
  @Input() formattedStartDate: string | null = '';
  @Input() formattedEndDate: string | null = '';

  @Output() expandChart = new EventEmitter<any>();
  @Input() showExpandButton: boolean = true;

  lineChartData: ChartData = this.initializeChartData();
  lineChartOptions: ChartOptions = this.initializeChartOptions();

  @ViewChild('chartRef') chartComponent!: UIChart;

  constructor(
    private deviceService: DeviceService,
    private cdr: ChangeDetectorRef,
  ) { }

  private readonly unitMappings: Record<string, string> = {
    temperature: 'C',
    tvoc: 'ppb',
    humidity: '%',
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDeviceIDs'] || changes['selectedMeasure'] || changes['selectedDuration'] || changes['formattedStartDate'] || changes['formattedEndDate']) {
      this.loadChartData();
    }
  }

  initializeChartData(): ChartData {
    return {
      labels: [],
      datasets: [
        {
          label: '',
          data: [],
          borderColor: ['#AC2424'],
          borderWidth: 3,
          fill: false,
        },
      ],
    };
  }

  getChartImage(): string | null {
    if (!this.chartComponent?.chart) return null;
    return this.chartComponent.chart.toBase64Image();
  }

  getChartCSV(): { header: string[]; rows: any[][] } | null {
    if (!this.lineChartData) return null;
    const labels = this.lineChartData.labels;
    const datasets: any[] = this.lineChartData.datasets;
    const header = [
      'Time',
      ...datasets.map(d => d.fullLabelWithStats || d.label)
    ];
    const rows = labels.map((label, i) => {
      const row: any[] = [label];
      datasets.forEach(ds => {
        row.push(ds.data[i] ?? '');
      });
      return row;
    });
    return { header, rows };
  }

  initializeChartOptions(): ChartOptions {
    return {
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: { title: { display: true, text: '' } },
        y: { title: { display: true, text: '' } },
      },
    };
  }

  computeStats(values: number[]) {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;

    return {
      min: min.toFixed(2),
      max: max.toFixed(2),
      avg: avg.toFixed(2)
    };
  }

  truncateMiddle(text: string, maxLength: number = 10): string {
    if (!text || text.length <= maxLength) {
      return text;
    }

    const charsToShow = maxLength - 3; // for "..."
    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    return text.substring(0, frontChars) + '...' + text.substring(text.length - backChars);
  }


  private formatChartData(stats: any): ChartData {
    if (!stats || Object.keys(stats).length === 0) {
      return this.initializeChartData();
    }
    const datasets = Object.keys(stats).map((key, index) => {
      const values = stats[key].map((d: { measure_value: number }) => d.measure_value);
      const stat = this.computeStats(values);
      const device = this.iaqDevices.find(device => device.id.toString() === key);
      const fullLabel = device?.label ?? key;
      const deviceLabel = this.truncateMiddle(device?.label ?? key, 9);
      const fullLabelWithStats = `${fullLabel} (Min:${stat.min}|Max:${stat.max}|Avg:${stat.avg})`
      return {
        label: `${deviceLabel} (Min:${stat.min}|Max:${stat.max}|Avg:${stat.avg})`,
        fullLabel: fullLabel,
        fullLabelWithStats: fullLabelWithStats,
        data: stats[key].map((d: { measure_value: any; }) => d.measure_value),
        dataTime: stats[key].map((d: any) => d.time),
        fill: false,
        borderColor: [this.iaqDevices.find(device => device.id.toString() === key)?.color ?? '#e6194b'],
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 12
      };
    });

    const firstKey = Object.keys(stats)[0];
    const firstDeviceData = stats[firstKey];
    const timeList = firstDeviceData.map((entry: { time: any; }) => entry.time);
    const labels = stats[Object.keys(stats)[0]].map((d: { time: string; }, index: number) => this.formatChartLabels(d.time, index, timeList));

    return {
      labels: labels,
      datasets: datasets
    };
  }

  private formatChartLabels(time: string, index: number, labels: string[]): string {
    const dateObj = new Date(time);
    const currentDate = dateObj.toLocaleDateString();
    const currentTime = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (this.selectedDuration === '1D') {
      const previousDate = index > 0 ? new Date(labels[index - 1]).toLocaleDateString() : null;

      // If it's the first label or the date changes, show date + time
      return previousDate !== currentDate ? `${currentDate} ${currentTime}` : currentTime;
    }

    // For other durations, show only the date
    return currentDate;
  }

  private formatYAxisLabel(measure: string): string {
    return measure === "mold" ? 'Mold Risk' : measure === "thermal_comfort_score" ? 'Thermal Comfort' : measure.toLowerCase() === 'tvoc' ? measure.toUpperCase() : this.capitalize(measure);
  }

  private capitalize(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  private getUnit(measure: string): string {
    if (measure === 'mold' || measure === 'thermal_comfort_score') {
      return this.unitMappings[measure.toLowerCase()] || '';
    }
    return `(${this.unitMappings[measure.toLowerCase()] || ''})`;
  }


  private loadChartData() {
    this.deviceService.getDeviceIAQStats(this.selectedDeviceIDs, this.selectedMeasure, this.selectedDuration?.toLowerCase() ?? null, this.formattedStartDate, this.formattedEndDate).subscribe({
      next: (data: any) => {
        this.lineChartData = this.formatChartData(data);
        this.lineChartOptions = {
          ...this.lineChartOptions,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            ...this.lineChartOptions.plugins,
            legend: {
              display: true,  // Show legend with line names
              position: 'top',
            },
            tooltip: {
              enabled: true,
              position: 'nearest',
              yAlign: 'top',
              xAlign: 'center',
              callbacks: {
                title: (context: any) => {
                  const index = context[0].dataIndex;

                  // get original time from dataset
                  const rawTime = context[0].dataset.dataTime?.[index];

                  if (!rawTime) return '';

                  const date = new Date(rawTime);

                  return date.toLocaleString([], {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                },
                label: (context: any) => {
                  const dataset = context.dataset;
                  // Remove stats part from label
                  const deviceLabel = dataset.fullLabel || dataset.label;
                  return `${deviceLabel}: ${context.parsed.y}`;
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: this.selectedDuration === '1D' ? 'Time' : 'Date',
                font: {
                  family: 'Proxima Nova, Arial, sans-serif',  // Specify font family
                  size: 12,                     // Font size
                  weight: 'normal',                // Font weight
                  style: 'normal',              // Font style
                },
                color: '#747574'
              },
              ticks: {
                font: {
                  family: 'Proxima Nova, Arial, sans-serif',  // Specify font family
                  size: 12,
                  weight: 'normal',                // Font weight
                  style: 'normal',              // Font style
                },
                color: '#747574'
              },
              grid: {
                color: '#00000036', // Horizontal grid lines (X axis)
                lineWidth: 1,
              },
            },
            y: {
              title: {
                display: true,
                text: `${this.formatYAxisLabel(this.selectedMeasure)} ${this.getUnit(this.selectedMeasure)}`,
                font: {
                  family: 'Proxima Nova, Arial, sans-serif',  // Specify font family
                  size: 12,                     // Font size
                  weight: 'normal',                // Font weight
                  style: 'normal',              // Font style
                },
                color: '#747574'
              },
              ticks: {
                font: {
                  family: 'Proxima Nova, Arial, sans-serif',  // Specify font family
                  size: 12,
                  weight: 'normal',                // Font weight
                  style: 'normal',              // Font style
                },
                color: '#747574'
              },
              grid: {
                color: '#00000036', // Vertical grid lines (Y axis)
                lineWidth: 1,
              },
            },
          },
        };
        this.cdr.detectChanges();
      },
      error: (error) => {
        alert('Failed to load device statistics.')
      }
    }
    );
  }

  openFullscreen() {
    this.expandChart.emit({
      selectedDeviceIDs: this.selectedDeviceIDs,
      selectedMeasure: this.selectedMeasure,
      selectedDuration: this.selectedDuration,
      formattedStartDate: this.formattedStartDate,
      formattedEndDate: this.formattedEndDate,
      iaqDevices: this.iaqDevices
    });
  }
}
